<template>
    <div
        v-if="isShowResult"
        class="result"
        data-testid="bet-result"
    >
        <template v-if="isNextGoal">
            <strong> {{ scoreText }}</strong>
            <span>
                {{ bet.fullTimeScore.home }}:{{ bet.fullTimeScore.away }}
            </span>
        </template>
        <template v-else-if="matchResultText && !isOutright && isDisplayMainMarket">
            <strong>{{ resultText }} | </strong>
            <span>{{ matchResultText }}</span>
        </template>
    </div>
    <div v-if="isDynamic && bet.isSettled" class="result dynamicResult">
        <Skeleton v-if="isLoadingDynamic" />
        <span v-else-if="dynamicWonResult?.wonSelectionName">
            <strong>{{ resultText }}:</strong>
            {{ dynamicWonResult.wonSelectionName }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, toRefs } from 'vue';
    import Skeleton from '@/components/common/skeleton/Skeleton.vue';
    import { Api } from '@/core/lib/api';
    import { getDaysDiff, getTodayWithGMT } from '@/core/lib/date';
    import { i18n } from '@/core/lib/i18n';
    import { isDynamicMarket, nextGoalMarketGroups } from '@/core/lib/oddsHelper';
    import type { IBet, ISubBet } from '@/interface/IBet';
    import type { Score } from '@/interface/ILiveScore';
    import type { IDynamicMarketWonResult } from '@/interface/IResult';
    import { BetTypeGroup, MarketType, SportType, TimezoneOffset } from '@/interface/enum';

    function formatFinalResult(bet: IBet | ISubBet) {
        if (!hasScore(bet.fullTimeScore)) {
            return '';
        }

        return i18n.t('statement_bet_detail_result_final', {
            homeFullTimeScore: bet.fullTimeScore.home,
            awayFullTimeScore: bet.fullTimeScore.away,
        });
    }

    function getSetScoreFromResult(result: string) {
        // Avoid Array.prototype.at(): Chrome < 92 loads the modern bundle, which plugin-legacy does not polyfill.
        const sets = result.split(',');
        return sets[sets.length - 1];
    }

    function formatSetResult(bet: IBet | ISubBet) {
        const setScore = bet.result ? getSetScoreFromResult(bet.result) : '';

        if (!setScore) {
            return '';
        }

        const [homeFullTimeScore, awayFullTimeScore] = setScore.split(':');

        return i18n.t('statement_bet_detail_result_set', {
            homeFullTimeScore,
            awayFullTimeScore,
        });
    }

    function formatMuayThaiResult(bet: IBet | ISubBet) {
        const { fullTimeScore } = bet;
        const maxRound = Math.max(fullTimeScore.home, fullTimeScore.away);

        if (maxRound <= 5) {
            return i18n.t('results_muay_thai_ko', { round: maxRound });
        }

        return i18n.t('results_muay_thai_round_completed', { round: 5 });
    }

    const isRankingScore = (sportType: SportType) => [SportType.MotorSport].includes(sportType);

    function formatWinnerResult(bet: IBet | ISubBet) {
        const { sportType, fullTimeScore, homeTeamName, awayTeamName } = bet;

        if (isRankingScore(sportType)) {
            return fullTimeScore.home < fullTimeScore.away ? homeTeamName : awayTeamName;
        }

        if (fullTimeScore.home === fullTimeScore.away) {
            return i18n.t('draw');
        }

        return fullTimeScore.home > fullTimeScore.away ? homeTeamName : awayTeamName;
    }

    function formatHTResult(score: Score) {
        return i18n.t('statement_bet_detail_result_ht', {
            homeHalfTimeScore: score.home,
            awayHalfTimeScore: score.away,
        });
    }

    function formatFTResult(score: Score) {
        return i18n.t('statement_bet_detail_result_ft', {
            homeFullTimeScore: score.home,
            awayFullTimeScore: score.away,
        });
    }

    function formatHTFTResult(bet: IBet | ISubBet) {
        const { halfTimeScore, fullTimeScore } = bet;
        const results = [];

        if (hasScore(halfTimeScore)) {
            results.push(formatHTResult(halfTimeScore));
        }

        if (hasScore(fullTimeScore)) {
            results.push(formatFTResult(fullTimeScore));
        }

        return results.join(', ');
    }

    const sportTypeResultFormatterMap = new Map<SportType, (bet: IBet|ISubBet) => string>([
        [SportType.Basketball, formatFinalResult],
        [SportType.Darts, formatFinalResult],
        [SportType.BeachSoccer, formatFinalResult],
        [SportType.Golf, formatFinalResult],
        [SportType.Badminton, formatSetResult],
        [SportType.Tennis, formatSetResult],
        [SportType.Volleyball, formatSetResult],
        [SportType.BeachVolleyball, formatSetResult],
        [SportType.TableTennis, formatSetResult],
        [SportType.Squash, formatSetResult],
        [SportType.Cricket, formatWinnerResult],
        [SportType.Cycling, formatWinnerResult],
        [SportType.Athletics, formatWinnerResult],
        [SportType.Boxing, formatWinnerResult],
        [SportType.MotorSport, formatWinnerResult],
        [SportType.MixedMartialArts, formatWinnerResult],
        [SportType.MuayThai, formatMuayThaiResult],
    ]);

    function matchResult(bet: IBet | ISubBet) {
        const { sportType } = bet;
        const sportTypeResultFormatter = sportTypeResultFormatterMap.get(sportType) ?? formatHTFTResult;
        return sportTypeResultFormatter(bet);
    }

    const props = defineProps<{
        bet: IBet | ISubBet;
    }>();

    const { bet } = toRefs(props);

    const isNextGoal = computed(() => nextGoalMarketGroups.includes(bet.value.betTypeGroup.id));
    const isShowResult = computed(() => bet.value.isSettled);

    const isDynamic = computed(() => isDynamicMarket(bet.value.marketType));
    const resultText = computed(() => i18n.t('statement_result'));
    const scoreText = computed(() => i18n.t('score'));
    const matchResultText = computed(() => matchResult(bet.value));
    const isOutright = computed(() => bet.value.marketType === MarketType.OutRight);
    const isDisplayMainMarket = computed(() => bet.value.betTypeGroup.id === BetTypeGroup.Main);

    function hasScore(score: Score) {
        return score.home !== -1 && score.away !== -1;
    }

    function isWithinThreeDays(): boolean {
        const today = getTodayWithGMT(TimezoneOffset.Server);
        const transDate = new Date((bet.value as IBet).transactionDate);
        transDate.setHours(0, 0, 0, 0);
        return getDaysDiff(today, transDate) <= 3;
    }

    const dynamicWonResult = ref<IDynamicMarketWonResult | null>(null);
    const isLoadingDynamic = ref(false);

    async function fetchDynamicResult() {
        if (!isDynamic.value || !bet.value.isSettled || !isWithinThreeDays()) return;
        const { sportType, matchId, selectionId } = bet.value as IBet;
        try {
            isLoadingDynamic.value = true;
            dynamicWonResult.value = await Api.getDynamicMarketResultBySelection(sportType, matchId, selectionId);
        } catch (error) {
            console.error(error);
            dynamicWonResult.value = null;
        } finally {
            isLoadingDynamic.value = false;
        }
    }

    fetchDynamicResult();
</script>

<style lang="scss" scoped>
    @import "@/components/myBets/Result";
</style>
